import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useAuth } from './use-auth';

export interface OrgSummary {
  id: string;
  name: string;
}

export interface UseOrgReturn {
  orgs: OrgSummary[];
  activeOrg: OrgSummary | null;
  activeOrgId: string | null;
  orgName: string;
  isLoading: boolean;
}

/**
 * Single source of truth for the active organisation.
 *
 * Wraps queryKey: ['organizations'] so all callers share one cached request.
 * Derives activeOrg from the user's primaryOrganization (from useAuth), falling
 * back to the first org in the list.
 */
export function useOrg(): UseOrgReturn {
  const { isAuthenticated, user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: isAuthenticated,
  });

  const orgs: OrgSummary[] = (data?.organizations ?? []).map((o) => ({
    id: o.id ?? '',
    name: o.name ?? '',
  }));

  // Strictly validate that primaryOrg still exists in the fetched list —
  // the user may have been removed from that org since the last auth refresh.
  // If it's gone, fall back to orgs[0]. If no orgs exist, return null.
  const primaryOrgId = user?.primaryOrganization?.id ?? null;
  const activeOrg =
    (primaryOrgId != null ? orgs.find((o) => o.id === primaryOrgId) : null) ?? orgs[0] ?? null;

  return {
    orgs,
    activeOrg,
    activeOrgId: activeOrg?.id ?? null,
    orgName: activeOrg?.name ?? 'My Organization',
    isLoading,
  };
}
